import React from 'react'
import { useTranslation } from 'react-i18next'

import Typography from '@mui/material/Typography'
import TableContainer from '@mui/material/TableContainer'
import Table from '@mui/material/Table'
import TableHead from '@mui/material/TableHead'
import TableBody from '@mui/material/TableBody'
import TableRow from '@mui/material/TableRow'
import TableCell from '@mui/material/TableCell'

import { Profile } from '../../types/profile'
import { EndCondition, EndConditionString } from '../../types/enums/endCondition'
import { msToTime, toReadableTimestamp } from '../../utils/dateUtils'
import { GraphDataParamString } from '../../types/enums/graphDataParam'
import { getMainProfileParam, isBtBasedProfile } from '../../utils/profileEditorUtils'
import { SeriesType } from '../../types/enums/seriesType'
import { getValueSuffix } from '../../utils/graphUtils'

interface ProfileDiffProps {
  selectedProfiles: Array<Profile> // this array takes 2, and only 2 profiles. All values are in correct temp scale
}

const ProfileDiff = (props: ProfileDiffProps): JSX.Element => {
  const { t } = useTranslation()

  const createPointDiffs = (
    thisArray: number[][] | null,
    oldArray: number[][] | null,
    propName: string,
    seriesType: SeriesType
  ): { propName: string; newValue: string | number; oldValue: string | number } => {
    const pointString = (time: number, value: number, seriesType: SeriesType): string => {
      const xValue = isBtBasedProfile(props.selectedProfiles[0].profile_type)
        ? time + getValueSuffix(SeriesType.TEMPERATURE)
        : msToTime(time)
      return `${xValue} ${value}${getValueSuffix(seriesType)}`
    }
    return {
      propName: propName,
      newValue: (thisArray || []).map((x) => pointString(x[0], x[1], seriesType)).join('\n'),
      oldValue: (oldArray || []).map((x) => pointString(x[0], x[1], seriesType)).join('\n'),
    }
  }

  const getEndConditionValue = (endCondition: EndCondition, endConditionValue: number): string | number => {
    return endCondition === EndCondition.DevelopmentTimeTarget || endCondition === EndCondition.RoastingTimeTarget
      ? msToTime(endConditionValue)
      : endConditionValue
  }

  const isArrayDifferent = (newArray: number[][] | null, oldArray: number[][] | null): boolean => {
    return JSON.stringify(newArray || []) !== JSON.stringify(oldArray || [])
  }

  const calculateDiffs = (): Array<{ propName: string; newValue: string | number; oldValue: string | number }> => {
    const thisProfile = props.selectedProfiles[0]
    const oldProfile = props.selectedProfiles[1]

    const diffs: Array<{ propName: string; newValue: string | number; oldValue: string | number }> = []

    if (thisProfile.name !== oldProfile.name) {
      diffs.push({
        propName: t('editprofileview.profilename'),
        newValue: thisProfile.name,
        oldValue: oldProfile.name,
      })
    }
    if (thisProfile.batch_weight !== oldProfile.batch_weight) {
      diffs.push({
        propName: t('editprofileview.batchweight'),
        newValue: thisProfile.batch_weight,
        oldValue: oldProfile.batch_weight,
      })
    }
    if (thisProfile.preheat_temperature !== oldProfile.preheat_temperature) {
      diffs.push({
        propName: t('editprofileview.preheattemp'),
        newValue: thisProfile.preheat_temperature,
        oldValue: oldProfile.preheat_temperature,
      })
    }
    if (thisProfile.end_condition !== oldProfile.end_condition) {
      diffs.push({
        propName: t('editprofileview.endcondition'),
        newValue: t(EndConditionString[thisProfile.end_condition]),
        oldValue: t(EndConditionString[oldProfile.end_condition]),
      })
      diffs.push({
        propName: t(EndConditionString[thisProfile.end_condition]),
        newValue: getEndConditionValue(thisProfile.end_condition, thisProfile.end_condition_value),
        oldValue: '-',
      })
    }

    if (
      thisProfile.end_condition === oldProfile.end_condition &&
      thisProfile.end_condition_value !== oldProfile.end_condition_value
    ) {
      diffs.push({
        propName: t(EndConditionString[thisProfile.end_condition]),
        newValue: getEndConditionValue(thisProfile.end_condition, thisProfile.end_condition_value),
        oldValue: getEndConditionValue(oldProfile.end_condition, oldProfile.end_condition_value),
      })
    }

    if (isArrayDifferent(thisProfile.temperature_bezier, oldProfile.temperature_bezier)) {
      diffs.push(
        createPointDiffs(
          thisProfile.temperature_bezier,
          oldProfile.temperature_bezier,
          t(GraphDataParamString[getMainProfileParam(thisProfile.profile_type)]),
          SeriesType.TEMPERATURE
        )
      )
    }

    if (isArrayDifferent(thisProfile.power_bezier, oldProfile.power_bezier)) {
      diffs.push(
        createPointDiffs(
          thisProfile.power_bezier,
          oldProfile.power_bezier,
          t('editprofileview.power'),
          SeriesType.PERCENT
        )
      )
    }

    if (isArrayDifferent(thisProfile.fan_bezier, oldProfile.fan_bezier)) {
      diffs.push(
        createPointDiffs(
          thisProfile.fan_bezier,
          oldProfile.fan_bezier,
          t('editprofileview.fanspeed'),
          SeriesType.PERCENT
        )
      )
    }

    if (isArrayDifferent(thisProfile.rpm_bezier, oldProfile.rpm_bezier)) {
      diffs.push(
        createPointDiffs(thisProfile.rpm_bezier, oldProfile.rpm_bezier, t('editprofileview.drumrpm'), SeriesType.RPM)
      )
    }

    if (thisProfile.metadata_image?.id !== oldProfile.metadata_image?.id) {
      diffs.push({
        propName: t('editprofileview.metadataimage'),
        newValue: thisProfile.metadata_image?.id ? t('editprofileview.newimage') : t('editprofileview.noimage'),
        oldValue: oldProfile.metadata_image?.id ? t('editprofileview.oldimage') : t('editprofileview.noimage'),
      })
    }

    if (thisProfile.notes !== oldProfile.notes) {
      diffs.push({
        propName: t('editprofileview.metadatanotes'),
        newValue: thisProfile.notes || '',
        oldValue: oldProfile.notes || '',
      })
    }

    if (JSON.stringify(thisProfile.selected_filters) !== JSON.stringify(oldProfile.selected_filters)) {
      diffs.push({
        propName: t('editprofileview.metadatafilters'),
        newValue: thisProfile.selected_filters ? thisProfile.selected_filters.map((x) => x.label).join(', ') : '',
        oldValue: oldProfile.selected_filters ? oldProfile.selected_filters.map((x) => x.label).join(', ') : '',
      })
    }

    return diffs
  }

  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell></TableCell>
            <TableCell>{`${toReadableTimestamp(props.selectedProfiles[0].created)} (${t(
              'editprofileview.newer'
            )})`}</TableCell>
            <TableCell>{`${toReadableTimestamp(props.selectedProfiles[1].created)} (${t(
              'editprofileview.older'
            )})`}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {calculateDiffs().map((diff) => {
            return (
              <TableRow key={diff.propName}>
                <TableCell sx={{ verticalAlign: 'top' }}>
                  <Typography>{diff.propName}</Typography>
                </TableCell>
                <TableCell
                  sx={{
                    backgroundColor: 'success.main',
                    color: 'success.contrastText',
                    verticalAlign: 'top',
                    whiteSpace: 'pre-wrap',
                  }}
                >
                  <Typography>{diff.newValue}</Typography>
                </TableCell>
                <TableCell
                  sx={{
                    backgroundColor: 'error.main',
                    color: 'error.contrastText',
                    verticalAlign: 'top',
                    whiteSpace: 'pre-wrap',
                  }}
                >
                  <Typography>{diff.oldValue}</Typography>
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    </TableContainer>
  )
}

export default ProfileDiff
